import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Radio, Satellite, RefreshCw, AlertTriangle, CheckCircle2,
  ExternalLink, Key, User, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { formatTimeAgo } from "../lib/utils";
import { tenantLocale, tenantTz } from "../lib/company";

function fmtErr(detail) {
  if (!detail) return "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

/**
 * Telematics Guru integration card.
 *
 * Self-contained: owns its own state + API calls, polls the config endpoint
 * every 10 s for live status (last sync time + result). Lifted out of
 * SettingsPage so that file can stay focused on layout/RBAC and the TG
 * config can be reused or A/B-tested independently if we ever need to.
 *
 * Auth methods supported: `api_key` and `basic_auth`. There's a discover
 * helper that probes all TG regions to auto-find the host for NZ/AU users.
 */
export default function TelematicsGuruCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    instance_host: "",
    auth_method: "api_key",
    api_key: "",
    username: "",
    password: "",
    org_id: "",
    auto_sync: false,
    sync_interval_seconds: 60,
  });
  const [orgs, setOrgs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const refresh = useCallback(async (syncForm = false) => {
    try {
      const { data } = await api.get("/integration/telematics-guru/config");
      setCfg(data);
      if (syncForm) {
        setForm((f) => ({
          ...f,
          instance_host: data.instance_host || "",
          auth_method: data.auth_method || "api_key",
          username: data.username || "",
          org_id: data.org_id || "",
          auto_sync: !!data.auto_sync,
          sync_interval_seconds: data.sync_interval_seconds || 60,
        }));
      }
    } catch (err) {
      // Polled every 10s — don't surface a toast for transient errors.
      if (process.env.NODE_ENV !== "production") {
        console.debug("[TelematicsGuruCard] refresh failed:", err?.message || err);
      }
    }
  }, []);

  useEffect(() => { refresh(true); }, [refresh]);

  // Status-only refresh (skip syncForm so we don't wipe the user's typing).
  useEffect(() => {
    const t = setInterval(() => refresh(false), 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const buildPayload = () => {
    const payload = {
      instance_host: form.instance_host.trim(),
      auth_method: form.auth_method,
      org_id: form.org_id.trim(),
      auto_sync: form.auto_sync,
      sync_interval_seconds: Math.max(30, parseInt(form.sync_interval_seconds || 60)),
      username: form.auth_method === "basic_auth" ? form.username.trim() : "",
    };
    if (form.api_key && form.api_key.trim()) payload.api_key = form.api_key.trim();
    if (form.password) payload.password = form.password;
    return payload;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/integration/telematics-guru/config", buildPayload());
      setCfg(data);
      setForm((f) => ({ ...f, api_key: "", password: "" }));
      toast.success("Integration settings saved");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.put("/integration/telematics-guru/config", buildPayload());
      const { data } = await api.post("/integration/telematics-guru/test");
      if (data.needs_org_id) {
        setOrgs(data.organisations || []);
        toast.message(`Connected! Pick an organisation below (${(data.organisations || []).length} available).`);
      } else {
        toast.success(`Connection OK — ${data.asset_count} assets reachable.`);
      }
      refresh();
      setForm((f) => ({ ...f, api_key: "", password: "" }));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const loadOrgs = async () => {
    setLoadingOrgs(true);
    try {
      await api.put("/integration/telematics-guru/config", buildPayload());
      const { data } = await api.get("/integration/telematics-guru/organisations");
      setOrgs(data || []);
      if (!data?.length) toast.warning("No organisations returned.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setLoadingOrgs(false); }
  };

  const pickOrg = async (orgId) => {
    setForm((f) => ({ ...f, org_id: String(orgId) }));
    try {
      await api.put("/integration/telematics-guru/config", { ...buildPayload(), org_id: String(orgId) });
      toast.success(`Organisation #${orgId} selected. You can now Sync Now.`);
      refresh();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };

  const discoverHost = async () => {
    if (!form.username.trim() || !form.password) {
      toast.error("Enter your TG username and password — we'll auto-find your region.");
      return;
    }
    setDiscovering(true);
    try {
      const { data } = await api.post("/integration/telematics-guru/discover-host", {
        username: form.username.trim(),
        password: form.password,
      });
      toast.success(`Found your region: ${data.host}. ${data.organisations.length} organisation(s) ready.`);
      setForm((f) => ({ ...f, instance_host: data.host, password: "" }));
      setOrgs(data.organisations);
      refresh(false);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setDiscovering(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integration/telematics-guru/sync");
      toast.success(`Synced ${data.assets_synced} assets · ${data.positions_updated} GPS updates`);
      refresh();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const statusOk = cfg?.last_sync_status === "ok";
  const statusErr = cfg?.last_sync_status === "error";

  return (
    <>
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-950 to-slate-900 text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-violet-600 flex items-center justify-center">
            <Satellite className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">Telematics Guru</div>
            <div className="text-xs text-slate-400">
              Digital Matter GPS fleet tracking ·{" "}
              <a
                href="https://support.digitalmatter.com/the-telematics-guru-api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:underline inline-flex items-center gap-0.5"
              >
                API docs <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
        <div>
          {cfg?.last_sync_at ? (
            statusOk ? (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-0" data-testid="sync-badge-ok">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Synced {formatTimeAgo(cfg.last_sync_at)}
              </Badge>
            ) : (
              <Badge className="bg-rose-500/20 text-rose-300 border-0" data-testid="sync-badge-err">
                <AlertTriangle className="h-3 w-3 mr-1" /> Error
              </Badge>
            )
          ) : (
            <Badge className="bg-slate-500/20 text-slate-300 border-0" data-testid="sync-badge-idle">Idle</Badge>
          )}
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <Label className="uppercase-label">API Instance Host</Label>
          <Input
            value={form.instance_host}
            onChange={(e) => setForm({ ...form, instance_host: e.target.value })}
            placeholder="api-apac03.telematics.guru"
            data-testid="tg-input-host"
          />
          <div className="text-xs text-slate-500 mt-1">
            Most New Zealand / Australia customers use <code className="bg-slate-100 px-1 py-0.5 rounded">api-apac03.telematics.guru</code>.
          </div>
        </div>

        <Tabs value={form.auth_method} onValueChange={(v) => setForm({ ...form, auth_method: v })} className="w-full">
          <TabsList className="bg-slate-100" data-testid="tg-auth-tabs">
            <TabsTrigger value="basic_auth" className="font-semibold" data-testid="tg-tab-basic">
              <User className="h-3.5 w-3.5 mr-1.5" /> Username + Password
            </TabsTrigger>
            <TabsTrigger value="api_key" className="font-semibold" data-testid="tg-tab-apikey">
              <Key className="h-3.5 w-3.5 mr-1.5" /> API Key
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic_auth" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="uppercase-label">TG Username (email)</Label>
                <Input type="email" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="you@yourcompany.co.nz" autoComplete="off" data-testid="tg-input-username" />
              </div>
              <div>
                <Label className="uppercase-label">TG Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={cfg?.password_set ? "•••••••••• — leave blank to keep stored" : "Your TG password"}
                  autoComplete="new-password" data-testid="tg-input-password" />
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-3">
              <div className="text-2xl">🪄</div>
              <div className="flex-1">
                <div className="font-semibold text-emerald-900 text-sm">Don&apos;t know your region?</div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Enter your TG email + password above and click this button — we&apos;ll probe all TG regional servers and auto-fill the right Instance Host for you.
                </div>
              </div>
              <Button onClick={discoverHost} disabled={discovering || !form.username.trim() || !form.password}
                className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white shrink-0" data-testid="tg-discover-host-btn">
                {discovering ? "Probing…" : "Auto-find region"}
              </Button>
            </div>

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-2.5">
              Your password is stored in the database and used only to fetch a fresh 24h bearer token from Telematics Guru. If you have MFA / 2FA enabled on your TG account, please disable it for this user — or switch to API Key.
            </div>
          </TabsContent>

          <TabsContent value="api_key" className="mt-4 space-y-4">
            <div>
              <Label className="uppercase-label">API Key</Label>
              <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder={cfg?.api_key_set ? `Stored: ${cfg.api_key_masked} — leave blank to keep` : "Paste your API key"}
                data-testid="tg-input-key" autoComplete="off" />
              <div className="text-xs text-slate-500 mt-1">
                Generate at: Account → Manage Account → Access tab → Generate (tick &ldquo;Store API key&rdquo; before leaving).
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div>
          <Label className="uppercase-label">Organisation ID</Label>
          <div className="flex gap-2">
            <Input value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })}
              placeholder="e.g. 1234" data-testid="tg-input-org" className="flex-1" />
            <Button variant="outline" onClick={loadOrgs} disabled={loadingOrgs || !form.instance_host} data-testid="tg-discover-orgs-btn">
              {loadingOrgs ? "Loading…" : "Discover…"}
            </Button>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Click <strong>Discover…</strong> to fetch the orgs your credentials can see, then pick one.
          </div>

          {orgs && orgs.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="tg-org-list">
              {orgs.map((o) => (
                <button key={o.id} type="button" onClick={() => pickOrg(o.id)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800/60 flex items-center justify-between"
                  data-testid={`tg-org-pick-${o.id}`}>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{o.name}</div>
                    <div className="text-xs text-slate-500">ID {o.id} · {o.asset_count ?? "?"} assets</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
            <Switch checked={form.auto_sync} onCheckedChange={(v) => setForm({ ...form, auto_sync: v })}
              data-testid="tg-auto-sync-switch" id="tg-auto" />
            <Label htmlFor="tg-auto" className="cursor-pointer">
              <div className="font-semibold text-slate-900 text-sm">Auto-sync</div>
              <div className="text-xs text-slate-500">Pull fleet + GPS automatically.</div>
            </Label>
          </div>
          <div>
            <Label className="uppercase-label">Sync Interval (seconds)</Label>
            <Input type="number" min={30} value={form.sync_interval_seconds}
              onChange={(e) => setForm({ ...form, sync_interval_seconds: e.target.value })}
              data-testid="tg-input-interval" />
            <div className="text-xs text-slate-500 mt-1">Min 30s. Recommended 60s.</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="tg-save-btn">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="tg-test-btn">
            <Radio className="h-4 w-4.5" />
            {testing ? "Testing…" : "Test Connection"}
          </Button>
          <Button variant="outline" onClick={syncNow} disabled={syncing || !form.org_id} data-testid="tg-sync-now-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        <div className="text-xs text-slate-500">
          <strong>Save</strong> writes your values to the DB · <strong>Test</strong> verifies them with TG · <strong>Sync Now</strong> imports assets (needs org ID).
        </div>

        {cfg?.last_sync_at && (
          <div className={`rounded-md p-3 text-sm border ${statusOk
            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="tg-last-sync">
            <div className="font-semibold flex items-center gap-1.5">
              {statusOk ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              Last sync · {formatTimeAgo(cfg.last_sync_at)} ({new Date(cfg.last_sync_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })})
            </div>
            {statusOk && (
              <div className="text-xs mt-1">
                {cfg.last_assets_synced} assets · {cfg.last_positions_updated} GPS positions updated
              </div>
            )}
            {statusErr && cfg.last_sync_error && (
              <div className="text-xs mt-1 font-mono">{cfg.last_sync_error}</div>
            )}
          </div>
        )}
      </div>
    </section>

    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
      <div className="font-display font-bold mb-1">How to set this up</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Enter your Telematics Guru host (almost certainly <code>api-apac03.telematics.guru</code> for NZ).</li>
        <li>Pick <strong>Username + Password</strong> and enter your TG login.</li>
        <li>Click <strong>Discover…</strong> to fetch the organisations you have access to, then click the one you want to sync.</li>
        <li>Click <strong>Test Connection</strong> — it should say &ldquo;Connection OK&rdquo;.</li>
        <li>Click <strong>Sync Now</strong> once to import your fleet. Each TG asset becomes a truck in your dispatch queue.</li>
        <li>Toggle <strong>Auto-sync</strong> on — GPS positions refresh every 60 seconds.</li>
      </ol>
    </div>
    </>
  );
}
